import { useRef, useState, useEffect, useCallback, ReactElement } from 'react'
import { toast } from 'sonner'
import { TOAST_STYLE } from './html'
import { useDrop, useDrag, XYCoord } from 'react-dnd'
import { Div,submerge,klass,applyLum,applySat,TKlass} from "./html"
import { ChainModule, ChainBlank, IMatrixModuleCell, IMatrixStubCell} from './module'
import { EditableTextBox, TargetIcon } from './widgets'
import { Tags } from './tag'
import { TriPop } from './menu'
import { getEmptyImage } from 'react-dnd-html5-backend'
import {IMatrixChain,IMatrixController,IMatrixCellItem,IDropZone, IMatrixCellItemController,IMatrixLane,
    IMatrixFill, IDragged, IDragObserver, IMidiController} from './logic'
import {IRoled,Chain,IChain,IModule,IChainHeaderController,IChainBoxController, ChainCellItem,
    Role, IModuleController, SubModule,
    TLatori} from './model'
import { IMidiLearn, MidiNote, IMidiNote } from './models/midi'

import { ChannelPort, IArch } from './arch'
import { Events } from './event'

const L = (...args) => window.console.log(...args)
const D = Div

// XXX put this somplace shared
const onClick = handler => (handler && {
    onClick: e => {
        e.button === 0 && handler && handler(e)
        e.stopPropagation()
    }
})

// TRACK Alt/Option key for copy-on-drag (gates copy semantics on cross-chain drops; also drives "+" cursor)
// Sticky: once Alt is pressed during a drag, copy mode latches — releasing Alt does not unlatch; only dragend resets.
let $altKey = false
let $nativeDrag = false
const $resetDrag = () => { $nativeDrag = false; $altKey = false }
const $onKey = (e:KeyboardEvent) => { if ($nativeDrag) { if (e.altKey) $altKey = true } else $altKey = e.altKey }
window.addEventListener('keydown',  $onKey)
window.addEventListener('keyup',    $onKey)
window.addEventListener('blur',     () => { if (!$nativeDrag) $altKey = false })
window.addEventListener('dragstart',() => { $nativeDrag = true })
window.addEventListener('dragend',  $resetDrag)

// TRACK Cmd key for the delete affordance: toggles body.cmd-held, which drives the
// delete-cursor + red overlay on hover (see chain.css `.cmd-held`). Live, not sticky —
// unlike $altKey above. Gated on the SAME Events.cmd() predicate as the click handlers
// (chain.tsx ChainHeader, module.tsx ChainModule), so the affordance only shows when a
// click would actually delete — cmd+shift/ctrl/alt show nothing.
let $cmdKey = false
const $onCmdKey = (e: KeyboardEvent) => {
    const held = Events.cmd(e)
    if (held === $cmdKey) return
    $cmdKey = held
    document.body.classList.toggle('cmd-held', held)
}
window.addEventListener('keydown', $onCmdKey)
window.addEventListener('keyup',   $onCmdKey)
window.addEventListener('blur',    () => { $cmdKey = false; document.body.classList.remove('cmd-held') })

// TRACK Alt/Option for the isolate (copy-to-new-chain) affordance: toggles body.alt-held,
// which drives the "+" cursor + green overlay on hover (see chain.css `.alt-held`). A SEPARATE,
// live tracker from $altKey above: that one is sticky-during-drag and reads raw altKey for
// drag-copy; this one mirrors $cmdKey — gated on Events.alt() so it matches the click handler
// (module.tsx ChainModule) and only shows when a click would actually isolate.
let $altHeld = false
const $onAltKey = (e: KeyboardEvent) => {
    const held = Events.alt(e)
    if (held === $altHeld) return
    $altHeld = held
    document.body.classList.toggle('alt-held', held)
}
window.addEventListener('keydown', $onAltKey)
window.addEventListener('keyup',   $onAltKey)
window.addEventListener('blur',    () => { $altHeld = false; document.body.classList.remove('alt-held') })
// Deferred: called from useEffect inside a DndProvider child, so this listener registers
// after react-dnd's dragover handler and fires second (DOM event order = registration order).
let $dragOverRegistered = false
function $registerDragOver () {
    if ($dragOverRegistered) return
    $dragOverRegistered = true
    window.addEventListener('dragover', e => {
        if (e.altKey) $altKey = true
        // ALWAYS set dropEffect: 'copy' when Alt held, 'move' otherwise.
        // Prevents browser's default copy badge from flashing at drag start.
        if (e.dataTransfer) e.dataTransfer.dropEffect = $altKey ? 'copy' : 'move'
    })
}

Array.prototype['tap'] = function(fn) {
    return this.map((item:any, index:number, array:any[]) => {
        typeof fn === 'function' && fn(item, index, array)
        return item
    })
}

export type IMatrixRenderEvent = {
    chain: string
    cell: string
    item: number
    pos: XYCoord
    channel: 'L'|'R'
}

export type MatrixRenderCB = (event:IMatrixRenderEvent) => void

export function MatrixModuleCellView ({module,dragging,overlay} : {
        module:IModule,
        dragging?:boolean,
        overlay?: {
            arch: IArch
            module?: IModule
        }
    })
    : IMatrixModuleCell
{
    const { id, name, source, dest, proc, L, R } = module
    // XXX why not respect `phonic` field of module?
    const stereo = !!L && !!R
    const phonic: TLatori | undefined = 
            SubModule.getPlace(module) === 'L'
            ? {L: true}
            : SubModule.getPlace(module) === 'R'
            ? {R: true}
            : undefined
    return {
        id,
        name,
        source, dest, proc,
        dragging: !!dragging,
        device: module.device,
        // TRANSFORM channel objects into simple labels
        channels: stereo ? {L,R} : {L},
        phonic,
        overlay,
    }
}

// VIEW MODEL - transform module for rendering
export class MatrixModuleCell {
    // CREATE from chain cell item
    static create ({cell,module,stub,dragging,overlay}: {
        cell: IMatrixCellItem
        module?: IModule
        stub?: boolean
        overlay?: {
            module?: IModule
            arch: IArch
        }
        dragging?:boolean
        })
    : IMatrixStubCell | IMatrixModuleCell
    {
        if (!module || !!stub) {
            return {
                stub: true,
                ...Role.create(cell),
            } as IMatrixStubCell
        }
        else {
            return MatrixModuleCellView({
                module,
                dragging,
                overlay,
            })
        }
    }
}

function BridgeLink ({active,thru,mult}) {
    return (
        <D k={['chain-cell-bridge']}>
            {active && <D k={['link']}/>}
            {active && mult && <D k={['tee']}/>}
            {!active && thru && <D k={['thru']}/>}
        </D>
    )
}

interface IDZoneOverride {
    zone?: string
    class?: string
    hover?: { s: {h: number, pt: number} }
    overlay?: { s: {h: number, pt: number} }
}

interface IDZone {
    before?: boolean
    upon?: boolean
    after?: boolean
    stereo?: boolean
    split?: boolean
    overrides?: {
        before?: IDZoneOverride
        upon?: IDZoneOverride
        after?: IDZoneOverride
    }
}

interface IMatrixLink extends IRoled {
    vert?: boolean
    terminal?: boolean
    thru?: boolean
    short?: boolean
    tee?: boolean
    children?: any
    split?: boolean
}

// ── Drag-and-drop zone resolution & hover feedback ────────────────────
// Unified zone resolver: maps cursor Y within a cell to before/upon/after,
// context-aware for AB-group positions and adjacent-to-AB cells.
function resolveZone (
    y: number,
    h: number,
    cell?: IMatrixCellItem,
    upper?: IMatrixCellItem | IMatrixFill,
    lower?: IMatrixCellItem | IMatrixFill,
) : 'before' | 'upon' | 'after' {
    const cy = Math.max(0, Math.min(y, h))
    if (cell?.abgroup) {
        if (!cell.abgroup.first && !cell.abgroup.last) {
            // ab_middle: ~13px "upon" band centered at midpoint
            const mid = Math.floor(h / 2)
            return cy < mid - 6 ? 'before' : cy > mid + 6 ? 'after' : 'upon'
        }
        // ab_first, ab_last (or both for single-item groups): centered 10px "upon" with 16px edges
        const start = Math.floor((h - 10) / 2)
        const end = start + 10
        return cy < Math.max(16, start) ? 'before' : cy >= Math.min(h - 16, end) ? 'after' : 'upon'
    }
    // outer_above_group: bias lower half toward AB
    if (lower && !lower.fill && lower.abgroup?.first) {
        return cy < 10 ? 'before' : cy >= Math.floor(h / 2) ? 'after' : 'upon'
    }
    // outer_below_group: bias upper half toward AB
    if (upper && !upper.fill && upper.abgroup?.last) {
        return cy < Math.floor(h / 2) ? 'before' : cy < h - 6 ? 'upon' : 'after'
    }
    // default
    return cy < 10 ? 'before' : cy < h - 6 ? 'upon' : 'after'
}

// Detects when an intra-chain drag would place the module back at its
// original position. Used to suppress no-op before/after drop zones.
function isNoopIntraDrag (
    dragItem: IMatrixCellItem | undefined,
    lane: IMatrixLane,
    cell: IMatrixCellItem,
    slotIndex: number,
    direction: 'before' | 'after',
) : boolean {
    if (!dragItem) return false
    const targetSlot = direction === 'after' ? slotIndex + 1 : slotIndex
    // AB-group case: compare within cell's item list
    if (cell.abgroup && dragItem.abgroup && dragItem.cell.id === cell.cell.id) {
        const sourceSlot = dragItem.abgroup.index
        const normalized = sourceSlot < targetSlot ? targetSlot - 1 : targetSlot
        return normalized === sourceSlot
    }
    // Lane-level case: compare slot indices within the same lane
    if (!cell.abgroup && dragItem.lane.id === lane.lane) {
        if (dragItem.abgroup) return false
        const sourceSlot = lane.cells.findIndex(c => !c.fill && c.cell.id === dragItem.cell.id)
        if (sourceSlot < 0) return false
        const normalized = sourceSlot < targetSlot ? targetSlot - 1 : targetSlot
        return normalized === sourceSlot
    }
    return false
}

// An outer processor cell: proc, not source/dest/AB/stub
function isOuterProc (item?: IMatrixCellItem | IMatrixFill) : boolean {
    return !!(item && !item.fill && item.proc && !item.abgroup && !item.stub)
}

interface IMatrixCellItemArgs {
    chain?: string
    midi?: IMidiLearn
    cell?: IMatrixCellItem
    fill?: IMatrixFill
    link?: IMatrixLink
    mult?: boolean | 'm2s'
    blank?: boolean
    lane: IMatrixLane
    bridged?: {
        thru: boolean
        active: boolean
        mult: boolean
    }
    dragging?: boolean
    ineligible?: boolean
    controllers?: {
        cell?: IMatrixCellItemController,
        module?: (node:IModule) => IModuleController,
    },
    onDrop?: (dropped: IDragged, zone: IDropZone, copy?: boolean) => void
    onAbHover?: (abId?: string, replace?: {cellId: string, item: number}) => void
    onRender?: ({pos}:{pos:XYCoord,channel:'L'|'R'}) => void
    dragSeer?: {
        stop: () => void
        start: (dragged: IDragged) => void
        item: IDragged
    }
    dzones?: IDZone
    upper?: IMatrixCellItem | IMatrixFill
    lower?: IMatrixCellItem | IMatrixFill
    focusedAbId?: string
    replaceTarget?: {cellId: string, item: number}
    split?: boolean
    overlay?: {
        module?: IModule
        arch: IArch
    }
}

export function MatrixCellItem (args : IMatrixCellItemArgs)
{
    const {
        chain,
        cell,
        fill,
        link,
        blank,
        mult,
        bridged,
        dragging,
        ineligible,
        dzones={},
        controllers,
        onDrop,
        onAbHover,
        onRender,
        dragSeer,
        upper,
        lower,
        focusedAbId,
        replaceTarget,
        overlay } = args

    const rootRef = useRef<HTMLDivElement>(null)
    const dropRef = useRef<HTMLDivElement>(null)
    const split = !!(cell||fill)?.split
    const isM2S = !!cell?.modules.L?.m2s

    // ENABLE dragging of CELL item
    //const cellDrag = isM2S || !split || !!bridged || cell?.conjoined 

    // IS CELL BEING DRAGGED?
    const [{dragging:$dragging},$drag,preview] = useDrag(() => ({
        type: 'cell',
        item: () => dragSeer ? dragSeer.item : undefined,
        end: dragSeer?.stop,
        collect: monitor => ({ dragging: monitor.isDragging() }),
    }),[rootRef,cell])

    // CAPTURE drag state
    const state = useRef($dragging)
    if ($dragging && !state.current) {
        dragSeer?.start(dragSeer.item)
    }
    state.current = $dragging

    if (dragSeer) {
        // INVISIBLE DRAG SOURCE: hides default drag preview (i.e. ghost)
        preview(getEmptyImage(),{ captureDraggingState: true })
        $drag(rootRef)
    }

    const GROUP_FRAME_W = 276
    let zone:IDropZone
    const [hoverClass, setHoverClass] = useState<string[]>([])

    // ── Role of this cell (used by gap-pocket and hitbox logic) ──
    const isItemOuterProc = !!cell && isOuterProc(cell)  // standalone proc (not AB, not stub)
    const isItemDest = !!(cell?.dest && !cell.abgroup) || !!fill?.dest

    // ── Hitbox extension ──
    // At standalone boundaries (source↔proc, proc↔dest), both sides extend
    // 20px toward each other so the pocket is easier to reach. One-sided
    // extension would steal the neighbor's drop zones, so we only extend when
    // the neighbor is also standalone (AB-grouped cells never qualify).
    const hasEdgeTopPocket = !!(
        (isItemOuterProc && upper && !upper.fill && upper.source && !upper.abgroup)  // source → proc
        || (isItemDest && isOuterProc(upper))                                         // proc → dest
    )
    const hasEdgeBottomPocket = !!(
        isItemOuterProc && lower && lower.dest && (lower.fill || !lower.abgroup)      // proc → dest (from proc side)
    )

    // Cross-role boundary: source (possibly AB) directly adjacent to dest AB first
    const isCrossRoleBoundary = !!(cell?.dest && cell.abgroup?.first && upper && !upper.fill && upper.source)
    // Two different AB groups touching — triggers the outerGap narrow pocket
    const hasAdjacentGroup = !!(
        cell?.abgroup?.first && upper && !upper.fill && upper.abgroup?.last && upper.abgroup.id !== cell.abgroup.id
        || cell?.abgroup?.last && lower && !lower.fill && lower.abgroup?.first && lower.abgroup.id !== cell.abgroup.id
    )
    const dropHitbox = {
        top: hasEdgeTopPocket ? 20 : 0,
        bottom: (cell?.abgroup?.last ? 5 : 0) + (hasEdgeBottomPocket ? 20 : 0),
    }
    const showDropHitbox = !!(onDrop && (dzones.before || dzones.upon || dzones.after))

    function getDropZone (rect:DOMRect,xy:XYCoord) : IDropZone {
        const {top,left} = rect
        const y = xy.y - top
        const x = xy.x - left
        const active = resolveZone(y, rect.height, cell, upper, lower)
        // Outer gap between adjacent AB groups
        const outerGap = !!(hasAdjacentGroup && cell?.proc && (
            cell?.abgroup?.first && active === 'before' && y <= 8
            || cell?.abgroup?.last && active === 'after' && y >= rect.height - 8
        ))
        return {
            upon: active === 'upon',
            before: active === 'before',
            after: active === 'after',
            outerGap,
            channel: (!split || x < GROUP_FRAME_W) ? 'L' : 'R',
        }
    }

    function canReceive (zone:IDropZone) : boolean {
        return !!(onDrop && (
            zone.before && dzones.before
            || zone.after && dzones.after
            || zone.upon && dzones.upon
        ))
    }

    // Apply overrides to an already-normalized zone (e.g. upon → before/after remap)
    function rezone (zone:IDropZone) : IDropZone {
        const {channel,outerGap} = zone
        const {upon,overrides} = dzones
        if (zone.upon && upon && overrides?.upon?.zone) {
            return { [overrides.upon.zone]: true, channel, outerGap }
        }
        return zone
    }

    // Normalize raw zone: if not allowed, remap to nearest allowed.
    // Branches ordered by specificity: general remaps first, context-specific overrides later.
    function normalizeZone (zone:IDropZone) : IDropZone {
        if (!zone.before && !zone.upon && !zone.after) return zone
        if (canReceive(zone)) return zone
        const {channel,outerGap} = zone
        // Disallowed before/after → remap to upon
        if ((zone.before || zone.after) && dzones.upon) {
            return { upon: true, channel, outerGap }
        }
        // outer_below_group: upon → before
        if (upper && !upper.fill && upper.abgroup?.last && zone.upon && dzones.before) {
            return { before: true, channel, outerGap }
        }
        // outer_above_group: upon → after
        if (lower && !lower.fill && lower.abgroup?.first && zone.upon && dzones.after) {
            return { after: true, channel, outerGap }
        }
        return zone
    }

    function getHoverClass (zone:IDropZone) {
        const vz = rezone(zone)
        const {overrides,stereo,split: replaceWholeSplit} = dzones
        // ── Gap pockets ──
        // A "gap pocket" is the centered blue pocket (dz-mid slot-gap-*) shown at
        // role boundaries between standalone cells. Without these, before/after
        // zones render as thin bars (dz-top/dz-bot).
        //
        // The rule: show a pocket when this cell is a standalone proc or dest and
        // the adjacent neighbor exists and isn't a fill. Chain layout is strictly
        // source → proc → dest, so "non-fill neighbor" always means a role boundary.
        // An empty destination fill uses its upon zone as the terminal insert
        // pocket. A real destination cell does not: upon means replacement, while
        // processor insertion is explicitly rezoned to before by dzones.overrides.
        const hasUpperBoundary = !!(upper && !upper.fill)
        const hasLowerBoundary = !!(lower && !lower.fill)
        const isGapBefore = !!(
            (vz.outerGap && vz.before)
            || ((vz.before || (vz.upon && !!fill?.dest))
                && (isItemOuterProc || isItemDest) && hasUpperBoundary)
        )
        const isGapAfter = !!(
            (vz.outerGap && vz.after)
            || (vz.after && isItemOuterProc && hasLowerBoundary)
        )
        const outerGapActive = !!(vz.outerGap && (vz.before || vz.after))
        const crossRoleBefore = vz.before && isCrossRoleBoundary
        const isOuterPocket = isGapBefore || isGapAfter
        const isOuterBelow = !!(upper && !upper.fill && upper.abgroup?.last)
        const isLowerGroupFirst = !!(lower && !lower.fill && lower.abgroup?.first)
        const isAbFirst = !!cell?.abgroup?.first
        const isAbLast = !!cell?.abgroup?.last
        const isReplace = !!(vz.upon && cell && !cell.abgroup)
        const isChannelReplace = !!(isReplace && split && !(stereo || replaceWholeSplit))

        const overlay = isOuterPocket
            ? 'dz-mid'
            : vz.before ? overrides?.before?.class || 'dz-top'
            : vz.upon ? overrides?.upon?.class || 'dz-mid'
            : overrides?.upon?.class || 'dz-bot'
        const semantic = isGapBefore ? 'slot-gap-before'
            : isGapAfter ? 'slot-gap-after'
            : vz.before ? 'slot-before'
            : vz.after ? 'slot-after'
            : 'slot-upon'
        const palette = isOuterPocket
            ? 'slot-outer'
            : vz.upon ? undefined
            : (!cell?.abgroup || crossRoleBefore) ? 'slot-outer' : undefined
        return [
            overlay,
            (zone.channel === 'R' && !(stereo || replaceWholeSplit)) ? 'right' : 'left',
            semantic,
            isReplace && 'replace-outline',
            isChannelReplace && 'replace-channel',
            isOuterBelow && vz.before && !crossRoleBefore && 'boundary-outer-bottom',
            isAbFirst && vz.before && !outerGapActive && !crossRoleBefore && 'ab-boundary-top',
            isAbLast && vz.after && !outerGapActive && 'ab-boundary-bottom',
            vz.after && isLowerGroupFirst && !cell?.abgroup && 'boundary-lower-group',
            palette,
        ].filter(Boolean) as string[]
    }

    const [{over=false}, drop] =
        useDrop({
            accept: ['module','cell'],
            drop: (item:IDragged,monitor) => {
                if (!(onDrop && zone && canReceive(zone))) return {nodrop: true}
                const isCrossChain = !!item.chain && item.chain !== chain
                onDrop(item,rezone(zone), isCrossChain ? $altKey : undefined)
            },
            collect: monitor  => ({
                over: !!monitor.isOver(),
                item: monitor.getItem(),
            }),
            hover: (_, mon) => {
                if (!rootRef.current) return
                const xy = mon.getClientOffset()
                if (!xy) return
                zone = normalizeZone(getDropZone(
                    rootRef.current.getBoundingClientRect(),
                    xy!,
                ))
                const receivable = canReceive(zone)
                setHoverClass(receivable ? getHoverClass(zone) : [])
                // Derive hover intent for AB focus ring + replace highlight
                if (cell?.abgroup && !zone.outerGap && receivable) {
                    onAbHover?.(
                        cell.abgroup.id,
                        zone.upon ? {cellId: cell.cell.id, item: cell.item} : undefined,
                    )
                } else {
                    onAbHover?.(undefined)
                }
            },
        })

    useEffect(() => {
        if (!over) {
            setHoverClass([])
            onAbHover?.(undefined)
        }
    }, [over, onAbHover])

    useEffect(() => {
        return () => onAbHover?.(undefined)
    }, [onAbHover])

    if (onDrop) drop(dropRef)

    // common group attributes
    function core (cell: IMatrixCellItem,channel: 'L' | 'R',node?: IModule) {
        const abFocus = !!cell.abgroup && focusedAbId === cell.abgroup.id
        return {
            split: cell.split,
            grouped: cell.abgroup && {
                index: cell.abgroup.gindex,
                count: cell.abgroup.count,
                top: cell.abgroup.first,
                bottom: cell.abgroup.last,
                abFocus,
                replaceTarget:
                    abFocus
                    && replaceTarget?.cellId === cell.cell.id
                    && replaceTarget?.item === cell.item,
            },
            bypassed: cell.bypassed ?? {},
            bypassable: true,
            ineligible,
            stub: !!cell.stub,
            proc: !!cell.proc,
            active: cell.active,
            channel,
            conjoined: cell.conjoined,
            color: cell.chain.state.color,
            midi: midi(node),
            controllers: {
                cell: controllers?.cell,
                module: node && controllers?.module?.(node)
            }
        }
    }

    const refL = useRef<HTMLDivElement>(null)
    const refR = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (onRender) {
            if (refL.current) {
                const rect = refL.current?.getBoundingClientRect()
                if (rect) {
                    onRender({ pos: {x:rect.x,y:rect.y - rect.height * 1.5}, channel: 'L' })
                }
            }
            if (refR.current) {
                const rect = refR.current?.getBoundingClientRect()
                if (rect) {
                    onRender({ pos: {x:rect.x,y:rect.y - rect.height * 1.5}, channel: 'R' })
                }
            }
        }
    })

    const L = 'L'
    const R = 'R'

    const midi = (module?: IModule) => {
        if (!chain || !module || !args.midi) return
        if (!args.midi) return
        const midi = args.midi!
        return {
            tab: cell?.abgroup
                ? {
                    click: () => midi.learn({chain,group:cell.cell.id}),
                    state: midi.state({chain,group:cell.cell.id}),
                }
                : undefined,
            L: {
                click: () => midi.learn({chain,node:module.id}),
                state: midi.state({chain,node:module.id}),
            },
            R: {
                click: () => midi.learn({chain,node:module.id}),
                state: midi.state({chain,node:module.id}),
            }
        }
    }

    // CELL contents
    const $cell = 
        // stereo split
        split
        ? (
            link ? (
                link.vert
                ? <><VertChainLink {...{...link,split:true,left:true}}/><VertChainLink {...{...link,split:true,right:true}}/></>
                : <>
                    <HorzChainLink {...{...link,split:true,left:true}}/>
                    <HorzChainLink {...{...link,split:true,right:true}}/>
                    {
                        link.thru && <D k={['chain-mult-link','thru']}/>
                    }
                </>
            )
            : (fill || blank) ? <><ChainBlank/><ChainBlank/></>
            : cell !== undefined &&
                // SPLIT STEREO module
                (cell.conjoined ?
                    <>
                        <Div ref={refL}>
                            <ChainModule {...{
                                chain,
                                side: {L:true},
                                module: cell.modules.L,
                                cell: MatrixModuleCell.create({cell,module:cell.modules.L,dragging,overlay}),
                                ...core(cell!,L,cell.modules.L),
                            }}/>
                        </Div>
                        <Div ref={refR}>
                            <ChainModule {...{
                                chain,
                                side: {R:true},
                                module: cell.modules.L,
                                cell: MatrixModuleCell.create({cell,module:cell.modules.R,dragging,overlay}),
                                ...core(cell,R,cell.modules.L),
                            }}/>
                        </Div>
                    </>
                // BRIDGED mono module
                : bridged ?
                    <>
                        <Div ref={refL}>
                            <ChainModule {...{
                                chain,
                                side: {L:true},
                                module: cell.modules.L,
                                cell: MatrixModuleCell.create({cell,module:cell.modules.L,dragging,overlay}),
                                ...core(cell,L,cell.modules.L),
                            }}/>
                        </Div>
                        <BridgeLink {...bridged}/>
                    </>
                // MONO-TO-STEREO
                :  isM2S ?
                    <>
                        <Div ref={refL}>
                            <ChainModule {...{
                                chain,
                                side: {L:true},
                                module: cell.modules.L,
                                cell: MatrixModuleCell.create({cell,module:cell.modules.L,dragging,overlay}),
                                m2s: true,
                                ...core(cell,L,cell.modules.L),
                            }}/>
                        </Div>
                        <BridgeLink {...{thru:true,active:true,mult}}/>
                    </>
                // DUAL mono modules
                :
                    <>
                        {
                            <Div ref={refL}>
                                <ChainModule {...{
                                    chain,
                                    side: {L:true},
                                    module: cell.modules.L,
                                    cell: MatrixModuleCell.create({cell,module:cell.modules.L,dragging,overlay}),
                                    ...core(cell,L,cell.modules.L),
                                    dragSeer,
                                    // XXX DOCME
                                    bypassable: core(cell,L).bypassable && !ChainCellItem.isEmpty({...cell.modules,split}),
                                }}/>
                            </Div>
                        }
                        {
                            <Div ref={refR}>
                                <ChainModule {...{
                                    chain,
                                    side: {R:true},
                                    module: cell.modules.R,
                                    cell:MatrixModuleCell.create({cell,module:cell.modules.R,dragging,overlay}),
                                    ...core(cell,R,cell.modules.R),
                                    dragSeer,
                                    // XXX DOCME
                                    bypassable: core(cell,R).bypassable,
                                    // explicit deletion of right-side module
                                    controllers: {
                                        cell: controllers?.cell
                                        ? {
                                            ...controllers.cell,
                                            delete: controllers.cell.delete ? () => controllers!.cell!.delete!('R') : undefined,
                                            activate: controllers.cell.activate,
                                            bridge: controllers.cell.bridge ? () => controllers!.cell!.bridge!('R') : undefined,
                                        } : undefined,
                                        module: cell.modules.R && controllers?.module ? controllers.module(cell.modules.R) : undefined,
                                    }
                                }}/>
                            </Div>
                        }
                    </>
            )
        )
        // UNSPLIT
        : (
            link ? (
                // LINK that is pseudo-split because source is unsplit/mono but is traversing
                // within a split lane (as would happen with an M2S module or mono-bridge)
                link.split ? (
                    link.vert
                    ? <><VertChainLink {...{...link,split:true,left:true}}/><VertChainLink {...{...link,spacer:true,split:true,right:true}}/></>
                    : <><HorzChainLink {...{...link,split:true,left:false}}/>
                        {
                            // RIGHT SIDE thru link of an unsplit mult source traversing a split lane
                            link.thru 
                            ? 
                                <>
                                    <HorzChainLink {...{...link,split:true,right:true,tee:false}}/>
                                    <D k={['chain-mult-link']}/>
                                </>
                            // XXX shouldn't really be necessary, we are just displaying the first link but this is needed to
                            // ensure alignment of the left-side link
                            : <HorzChainLink {...{...link,spacer:true}}/>
                        }
                    </>
                )
                : (
                    link.vert
                    ? <VertChainLink {...{...link}}/> 
                    : <>
                        <HorzChainLink {...{...link}}/> 
                        {link?.thru && <D k={['chain-mult-link','thru']}/>}
                    </>
                )
            )
            : (fill || blank) ? <ChainBlank/>
            : cell !== undefined &&
            (
                cell.stub 
                ? <ChainModule {...{
                    chain,
                    cell: MatrixModuleCell.create({cell,stub:true}),
                    ...core(cell,L),
                }}/>
                // NON-SPLIT cell
                :   <div ref={refL}> 
                        <ChainModule {...{
                            chain,
                            module: cell.modules.L,
                            cell: MatrixModuleCell.create({cell,module:cell.modules.L,dragging,overlay}),
                            ...core(cell,L,cell.modules.L),
                            active: cell.active
                        }}/>
                    </div>
            )
        )

    const $split = dzones?.split && 'split'
    const dzone = { k: ['dz',$split]}
    const calcZones = (right?:boolean) => {
        const chiral = right ? 'right' : 'left'
        return Object.assign({...dzone},dzones && {
            top: {
                hover: submerge({ k: ['top',chiral], }, {...dzone}),
                overlay: { k: ['dz','top-overlay',$split,chiral] },
            },
            mid: {
                hover: submerge(
                    dzones.overrides?.upon?.hover,
                    { k: ['mid',chiral] },
                    {...dzone}),
                overlay: submerge(
                    dzones.overrides?.upon?.overlay,
                    { k: ['mid-overlay',$split,chiral] },
                    {...dzone}),
            },
            bot: {
                hover: submerge({ k: ['bot',chiral] }, {...dzone}),
                overlay: { k: ['dz','bot-overlay',$split,chiral] },
            },
        })
    }

    const $zones = {
        l: calcZones(),
        r: calcZones(true),
    }

    return (
        <D ref={rootRef}
               k={[
                    'chain-cell',
                    (split || link?.split) && 'split',
                    over && hoverClass,
                    dragging && 'dragging',
               ]}
            >
            {onDrop && (
                <D
                    ref={dropRef}
                    k='drop-hitbox'
                    s={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        top: showDropHitbox ? -dropHitbox.top : 0,
                        bottom: showDropHitbox ? -dropHitbox.bottom : 0,
                        zIndex: showDropHitbox && (dropHitbox.top || dropHitbox.bottom) ? 6 : 5,
                        pointerEvents: showDropHitbox ? 'auto' : 'none',
                    }}
                />
            )}
            {dzones?.before &&
                <>
                    <D {...$zones.l.top.hover}/>
                    <D {...$zones.l.top.overlay}/>
                </>
            }
            {dzones?.before && split && (
                <>
                    <D {...$zones.r.top.hover}/>
                    <D {...$zones.r.top.overlay}/>
                </>
            )}
            {dzones?.upon && split &&
                <>
                    <D {...$zones.r.mid.hover}/>
                    <D {...$zones.r.mid.overlay}/>
                </>
            }
            {(dzones?.upon || dzones?.before || dzones?.after) &&
                <>
                    <D {...$zones.l.mid.hover}/>
                    <D {...$zones.l.mid.overlay}/>
                </>
            }
            {dzones?.after && 
                <>
                    <D {...$zones.l.bot.hover}/>
                    <D {...$zones.l.bot.overlay}/>
                </>
            }
            {dzones?.after && split && (
                <>
                    <D {...$zones.r.bot.hover}/>
                    <D {...$zones.r.bot.overlay}/>
                </>
            )}
            {$cell}
            {mult && (
                mult === 'm2s'
                // LONGER MULT CONNECTOR needed to bridge space left by downstream M2S module
                ?  <D k={['chain-mult-link','m2s']}/>
                :  <D k={['chain-mult-link']}/>
            )}
        </D>
    )
}

export function MatrixChain (
    {
        id,
        midi,
        chain,
        dragSeer,
        dragging,
        controllers,
        onRender:$onRender,
        overlay,
    } : 
    {
        id:string
        midi?:IMidiLearn
        chain:IMatrixChain
        dragSeer?:IDragObserver
        dragging?:IDragged
        controllers: {
            matrix: IMatrixController
            module: (node:IModule) => IModuleController
            midi: IMidiController
        }
        onRender?:MatrixRenderCB
        overlay?: {
            arch: IArch
        }
    })
{
    //D('------- MATRIX --------')
    useEffect($registerDragOver, [])
    const H = 44
    const $w = chain.lanes.length
    // ESTABLISH HEIGHT
    const $h = chain.lanes[0] ? chain.lanes[0].cells.length : 0
    const $lanes:IMatrixCellItemArgs[][] = []
    const [focusedAbId, setFocusedAbId] = useState<string>()
    const [replaceTarget, setReplaceTarget] = useState<{cellId: string, item: number}>()

    // CHAIN CELL being DRAGGED(if any)
    const dragItem = dragging?.item?.item
    const dragModule = dragging?.module
    const dragRole: IRoled = dragging?.role || {}
    if (dragging) L('DRAGGING',dragRole,dragging)
    const intraDrag = dragItem?.chain.id === id

    const handleAbHover = useCallback((abId?: string, replace?: {cellId: string, item: number}) => {
        setFocusedAbId(abId)
        setReplaceTarget(replace)
    }, [])

    useEffect(() => {
        if (!dragging) { setFocusedAbId(undefined); setReplaceTarget(undefined) }
    }, [dragging])

    // DISCOVER last position of chain's source (i.e. account for grouped source)
    // (enables connector styling (e.g. bg color))
    // XXX UNUSED???
    //const srcLast = chain.lanes[0]?.cells.filter(_ => _.source).length - 1

    // RENDER chain, by lane
    for (let i = 0; i < $w; i++) {
        const control = controllers?.matrix
        //D('------- LANE --------')
        const $lane:IMatrixCellItemArgs[] = []
        $lanes.push($lane)
        const lane = chain.lanes[i]
        // left/right-most lane
        const left = i == 0, right = i == $w - 1
        // TRACK AB-group status - specifically "active" item of AB-group
        const $abgroups = new Set()
        // track if dest filler has been terminated (marks the "arrow" that flows into lane's dest)
        let termed = false
        // encountered first cell of lane's source yet?
        let preSource = false
        // track link termination of lane - terminate last link (or first really, because we iterate backwards)
        const terminal = () => {
            const _ = termed
            termed = true
            return !_
        }
        let $next: IMatrixCellItemArgs | undefined
        let m2s = false
        let $$split = false
        // PROCEED backwards because it's simpler to implement termination styling (e.g. first observed "link" is terminal)
        for (let j = $h-1; j >= 0; j--) {
            // current matrix cell
            const $cell = lane.cells[j]
            if (!$$split) {
                $$split = $cell.split || !$cell.fill && $cell.split
            }
            // left/right cell
            const l = left ? null : chain.lanes[i - 1].cells[j], r = right ? null : chain.lanes[i + 1].cells[j]
            // prev/next cell
            const p = lane.cells[j - 1]
            const n = lane.cells[j + 1]
            // DETECT cycle
            // lane destination
            const $dest = lane.cells.slice(-1)[0]
            const cycle = 
                dragItem 
                && intraDrag 
                && (
                    $cell.fill === false && $cell.sources.includes(dragItem.cell.id)
                    ||
                    $cell.fill && !$dest.fill && $dest.sources.includes(dragItem.cell.id)
                )

            // DRAG MODULE is M2S
            const $dragM2S: IModule | undefined = dragModule?.m2s
                ? dragModule
                : dragItem?.modules.L?.m2s
                ? dragItem.modules.L
                : undefined

            // XXX what is this??!
            const dragM2S: string | undefined = $dragM2S && $cell.m2s?.id

            // RENDER "fill" cells 
            // SOURCE fill
            if ($cell.fill && $cell.source) {
                $lane.unshift({
                    lane,
                    fill: $cell,
                    blank: true,
                })
            }
            // DEST fill
            else if ($cell.fill && $cell.dest) {
                const fill = $cell
                // RECORD cell index (within lane) of first and last fill cells leading to destination (i.e. below last proc and above dest)
                // used to calculate drop zone size within fill region, below
                const fillZone = {
                    first: lane.cells.findIndex(_ => _.dest && _.fill),
                    last: lane.cells.findLastIndex(_ => _.dest && _.fill),
                }
                const preDest = lane.cells[fillZone.first - 1] as IMatrixCellItem
                // DIRECTLY above is us a source - extend up the "dropzone" by a single unit
                // XXX TBI
                const noProc = preDest.$source ? 1 : 0
                // ENTERED into DEST region?
                const destined = j > fillZone.last
                // DROP ZONE height (in cell units)
                const zh = fillZone.last - fillZone.first + 1
                // DROPZONE index (relative to fill cell's location)
                const zp = j - fillZone.first

                // if dropee originates from same chain we want to ensure it is not cell directly above the dest fill
                // cuz it's non-sensical to drop it here
                const dropMatch = intraDrag && dragItem && preDest && preDest.cell.id === dragItem.cell.id && preDest.item === dragItem.item
                // CREATE EXTRA-LARGE DROP ZONE in area between lane's last proc (or lane source) and dest
                const overrides = !(cycle || !dragRole.proc || destined || dropMatch || (dragM2S && fill.m2s))
                    ? {
                        before: { class: 'dz-mid' },
                        after: { class: 'dz-mid' },
                        upon: {
                            hover: { s: {h: H * zh, pt: -zp * H}},
                            overlay: { s: {h: H * zh, pt: -zp * H}},
                        },
                    }
                    : undefined

                // PRODUCE drop handler referring to destination cell (as opposed to actual receiver cell, link)
                const onDrop = control.onDrop &&
                    ((dropped:IDragged,zone:IDropZone,copy?:boolean) => control.onDrop({
                        dropped,
                        copy,
                        receiver: {
                            item: $cell,
                            lane: lane,
                            role: {dest: true},
                            chain: id,
                            // received zone always `before` for destination fills
                            zone: {
                                before: true,
                                channel: zone.channel,
                            },
                        },
                }))

                $lane.unshift({
                    chain: chain.chain.id,
                    lane,
                    fill,
                    upper: p,
                    lower: n,
                    onDrop,
                    dzones: {
                        stereo: dragItem?.lane.stereo, // XXX what about MODULE drop case?
                        // XXX this looks sus, isn't split just plain split?!
                        split: fill.split && (dragModule?.stereo || dragItem?.split),
                        before: !!overrides,
                        upon: !!overrides,
                        overrides,
                    },
                    link: {
                        vert: true,
                        terminal: terminal(),
                        proc: !destined,
                    },
                })
            }
            else {
                const cell = $cell as IMatrixCellItem
                // IS CELL mono-to-stereo processor?
                const isM2S = !!cell.modules.L?.m2s

                // TRACK presence of downstream M2S module. CRITICAL for link/mult visualizations.
                if (!m2s) {
                    m2s = isM2S
                }
                const {proc,dest,source,$source,abgroup,stub,active} = cell
                const linked = l?.fill === false && l?.cell.id === cell.cell.id
                    // *not* AB-group *or* AB-group with single valid item
                    //&& (!dragItem?.abgroup) // || dropee.count === 1)
                const {last,first} = abgroup || {}

                const canMult = source || proc
                const canAB = !abgroup && !isM2S && !!control.ab 
                const canUngroup = abgroup && first && n?.fill === false && n.stub
                // GROUP active state (keys off active state of first group)
                const isBypassed = !active.L

                // MARK encounter of "active" item of AB group - used below to determine connector choice for mult'ed group
                if (abgroup && !isBypassed) $abgroups.add(abgroup.id)

                // instersection of drag + receiver roles
                const $drag = {
                    source: dragRole.source && source,
                    proc: dragRole.proc && proc,
                    dest: dragRole.dest && dest,
                }

                const sameCell = cell.cell.id === dragItem?.cell.id
                const sameABItem = dragItem?.abgroup && dragItem.abgroup.index === abgroup?.index
                const upperAdjacentGroup = !!(cell.abgroup?.first && p && !p.fill && p.abgroup?.last && p.abgroup.id !== cell.abgroup?.id)
                const lowerAdjacentGroup = !!(cell.abgroup?.last && n && !n.fill && n.abgroup?.first && n.abgroup.id !== cell.abgroup?.id)
                const beforeNoop = !upperAdjacentGroup && isNoopIntraDrag(dragItem, lane, cell, abgroup ? abgroup.index : j, 'before')
                const afterNoop  = !lowerAdjacentGroup && isNoopIntraDrag(dragItem, lane, cell, abgroup ? abgroup.index : j, 'after')
                // edge-bottom-pocket: proc → dest boundary
                const edgeBottomPocketBefore = isOuterProc(p) && !!cell.dest && !cell.abgroup

                ////////////////////////////////////////////////////////////////////////////////
                // DERIVE "DROP ZONES" for MATRIX CELL
                // before, after + upon drop zones
                ////////////////////////////////////////////////////////////////////////////////
                const dzones: IDZone | undefined =
                    (
                        !dragging
                        // can't drop above lane source
                        || preSource
                        // NOT STEREO MODULE if upstream of M2S module
                        || (cell.upM2S && (dragModule?.stereo || dragItem?.split || dragItem?.stereo))
                    )
                    // NOTHING being dragged - NO DROP ZONES
                    ? undefined
                    : {
                        stereo: dragItem?.lane.stereo, // XXX what about MODULE drop case?
                        // XXX more split weirdness. why not just "cell.split" alone?!?!
                        split: cell.split && ((dragModule && (dragModule.stereo || !!dragM2S)) || dragItem?.lane.stereo || dragItem?.split),
                        // INSERT (BEFORE)
                        before:
                            (
                                // NOT M2S being dragged
                                !dragM2S
                                ||
                                (
                                    // CANNOT INSERT M2S in existing M2S path
                                    !cell.m2s
                                    &&
                                    // CANNOT INSERT M2S within AB-group
                                    (
                                        !cell.abgroup
                                        ||
                                        cell.abgroup.first
                                    )
                                )
                            )
                            // CANNOT INSERT before a lane source cell if ungrouped or "link"
                            && !(linked || source && !abgroup)
                            // block only normalized no-op/self-drops
                            && (!intraDrag || !cycle && !beforeNoop)
                            // NOT (AB-group last AND stub)
                            && !(last && stub)
                            && (
                                // drag/drop is proc
                                $drag.proc
                                // drag is proc, drop is dest AND ungrouped (OR group first w/o adjacent proc) AND previous is NOT a fill
                                || dragRole.proc && dest && (!abgroup || (first && !p?.proc)) && !p?.fill
                                // drag/drop is source AND grouped
                                || $drag.source && !!abgroup
                                // drag/drop is dest AND grouped
                                || $drag.dest && !!abgroup
                            )
                        ,
                        // REPLACE (UPON)
                        upon:
                            (
                                // NOT M2S dragged
                                !dragM2S
                                ||
                                // OR M2S dragged AND...
                                (
                                    // NOT AB-group
                                    !cell.abgroup
                                    &&
                                    (
                                        // NOT in existing M2S path
                                        !cell.m2s
                                        // BUT can drop upon another M2S module
                                        || isM2S
                                    )
                                )
                            )
                            // NOT lane source OR (lane source AND not linked)
                            && (!$source || $source && !linked)
                            // drag role == drop role (or dest cell in edge-bottom-pocket)
                            && ($drag.proc || $drag.source || $drag.dest
                                || dragRole.proc && dest && !!edgeBottomPocketBefore)
                            // intra-chain drag cases
                            && (!intraDrag || !cycle
                                // cannot drag AB-group item onto same AB-group's stub
                                && !(last && stub && sameCell)
                                && (
                                    (!stub && (!sameCell || abgroup && !sameABItem))
                                    // XXX this does not fully explain
                                    // intra-drag of non-AB cell to AB-group *stub*
                                    || (stub && (!sameCell || !!abgroup))
                                )
                            )
                            &&
                            // NOT M2S target OR otherwise dragged must also be M2S
                            (
                                !isM2S || !!dragM2S
                            )
                        ,
                        // INSERT (AFTER)
                        after:
                            // NOT M2S dragged AND (M2S path OR AB-group AND NOT LAST)
                            !(dragM2S && (cell.m2s || (cell.abgroup && !cell.abgroup.last)))
                            // NOT (after first item in AB group when last is stub)
                            && !(first && (n && !n.fill && n.abgroup?.last && n.stub))
                            // NOT last UNLESS AB-group non-stub (allow AFTER for real AB items)
                            && (!last || (abgroup && !stub))
                            // block only normalized no-op/self-drops
                            && (!intraDrag || !cycle && !afterNoop)
                            && (
                                // NOT (proc drag onto lane source) AND (ungrouped or group last) AND (grouped OR next is NOT a fill)
                                !($drag.proc && $source) && (!abgroup || last) && (!!abgroup || !n?.fill)
                                // NOT lane source OR (unlinked lane source) OR (linked lane source AND next is NOT a fill)
                                && (!$source || $source && !linked || $source && linked && !n?.fill)
                                && (
                                    // drag/drop is proc AND (grouped OR next is NOT a fill)
                                    $drag.proc && (!!abgroup || !n?.fill)
                                    // drag is proc, chain source AND ungrouped AND next is NOT a fill/dest;
                                    // grouped source never owns cross-role seam (dest-AB-first's before-zone does)
                                    || dragRole.proc && source && !abgroup && !n?.fill && !n?.dest
                                    // drag/drop is source AND grouped
                                    || $drag.source && !!abgroup
                                    // drag/drop is dest AND grouped
                                    || $drag.dest && !!abgroup
                                )
                            )
                        ,
                        overrides:
                            // drag/drop is proc AND lane source AND (ungrouped or group last)
                            $drag.proc && $source && (!abgroup || last)
                            ? {
                                // OVERRIDE 'upon' as 'after' if lane source to improve DND UX
                                upon: {
                                    zone: 'after',
                                    class: 'dz-bot',
                                    hover: { s: {h: H, pt: 0} },
                                    overlay: { s: {h: H/2, pt: H - 10} },
                                }
                            }
                            // dest cell in edge-bottom-pocket: rezone 'upon' to 'before'
                            : dest && !!edgeBottomPocketBefore && dragRole.proc
                            ? {
                                upon: {
                                    zone: 'before',
                                }
                            }
                            : undefined
                    }
                
                // MARK first encounter (really first, because iterating backwards) of lane's source - drop zones are computed differently after this
                if (!preSource) preSource = !!$source && (!abgroup || !!first)

                const link:IMatrixLink|undefined =
                    linked 
                    ? {
                        thru: r?.fill === false && r?.cell.id === cell.cell.id,
                        split: l && !l.split && $$split,
                        // XXX doc
                        short: $next && !($next.link || $next.blank),
                        tee: $source,
                        terminal: terminal(),
                    }
                    : undefined

                //if (link && !link.vert && (!link.thru || link.tee)) {
                if (link && (!link.thru || link.tee)) {
                    // TOP of LANE? decorate with delete lane icon
                    link.children =
                        <D k='delete' onClick={control.deleteLane ? () => control.deleteLane({lane:i}) : null}>
                            <span className="symbols">close</span>
                        </D>
                }

                // drop handler
                const onDrop = control.onDrop &&
                    ((dropped:IDragged,zone:IDropZone,copy?:boolean) => control.onDrop({
                        dropped,
                        copy,
                        receiver: {
                            chain: id,
                            item: cell,
                            lane: lane,
                            role: Role.create(cell),
                            zone,
                        },
                }))

                const dragCellItem : IDragged = {
                    role: Role.create(cell),
                    chain: chain.chain.id,
                    item: {
                        lane: lane,
                        item: cell,
                    }
                }

                const $onDrag =
                    dragSeer && !cell.stub // && (cell.abgroup || (cell.proc || !cell.conjoined && !cell.bridged))
                    // XXX doc
                    ? (() => {
                        return {
                            item: dragCellItem,
                            start: (dragged: IDragged) => dragSeer.start(dragged),
                            stop: dragSeer.stop,
                        }
                    })()
                    : undefined

                const onRender =
                    $onRender && !cell.stub
                    // XXX doc
                    ? ({pos,channel}:{pos:XYCoord,channel:'L'|'R'}) => {
                        $onRender({
                            pos,
                            channel,
                            chain: cell.chain.id,
                            cell: cell.cell.id,
                            item: cell.item,
                        })
                    }
                    : undefined

                // is cell (active) mult?
                const mult = 
                    // LINK is to right of this cell
                    (r?.fill === false && r.cell.id === cell.cell.id && 
                    // single item or active item of AB-group
                    ((!abgroup || abgroup && !isBypassed) || 
                    // LINK is to right of first cell of AB group that is completely disabled (all elements bypassed)
                    (abgroup && !$abgroups.has(abgroup.id) && first)))
                    ? (m2s ? 'm2s' : true)
                    : false

                const conjoined = !!(cell.split && cell.modules.L && cell.modules.L?.id === cell.modules.R?.id)
                const bridged = cell.bridged
                    ? {
                        // active AB-group item has not yet been encountered - draw thru symbol
                        thru: !!abgroup && isBypassed && !$abgroups.has(abgroup.id),
                        active: !isBypassed,
                        mult: !!mult,
                    }
                    : undefined

                const canBridge = cell.source && !!cell.split && !bridged && !conjoined
                const canSplit = cell.conjoined

                function noReceive (dzones: IDZone) : boolean {
                    return !dzones.after && !dzones.before && !dzones.upon
                }
                const dndProps = {
                    dzones,
                    upper: p,
                    lower: n,
                    onDrop,
                    onAbHover: handleAbHover,
                    focusedAbId,
                    replaceTarget,
                }

                $lane.unshift(
                    // RENDER matrix connector/spacer
                    link ? (
                        // mult'ed cell - horz connector
                        !abgroup ? {
                            cell,
                            lane,
                            link,
                            ...dndProps,
                        }
                        // MULT'ED AB-GROUP CELL
                        : (
                            // ACTIVE group cell
                            // (if not previously encountered active group cell and this is group first, we default to "active")
                            !isBypassed || (!$abgroups.has(abgroup.id) && first) ? {
                                cell,
                                lane,
                                link,
                                ...dndProps,
                                overlay,
                            }
                            // VERT "thru" connector (active group cell not SEEN, we are *below* active group)
                            : !$abgroups.has(abgroup.id) ? {
                                cell,
                                lane,
                                // group mult case - where blank should replace vert connectors of bypassed group cells
                                blank: !$source,
                                link: $source ? {...link,vert: !first} : undefined,
                                ...dndProps,
                                overlay,
                            }
                            // BLANK cell - (active group cell is SEEN, we are *above* active group)
                            : {
                                cell,
                                lane,
                                blank: true,
                                ...dndProps,
                                overlay,
                            }
                        )
                    )
                    // RENDER module cell
                    : {
                        cell: { ...cell },
                        lane,
                        chain: chain.chain.id,
                        ...dndProps,
                        bridged,
                        mult,
                        controllers: {
                            cell: {
                                ab: canAB ? () => control.ab({cell:cell.cell.id}) : undefined,
                                mult: !dest && canMult ? () => control.mult({cell:cell.cell.id}) : undefined,
                                activate: (channel,active) => control.activate({cell:cell.cell.id,item:cell.item,channel,active}),
                                delete: channel => control.deleteItem({cell:cell.cell.id,item:cell.abgroup?.index||0,channel}),
                                isolate: channel => control.isolateItem({cell:cell.cell.id,item:cell.abgroup?.index||0,channel}),
                                split: canSplit ? () => control.splitCell({cell:cell.cell.id,item:cell.item}) : undefined,
                                bridge: canBridge && cell.source ? channel => control.bridge({cell:cell.cell.id,item:cell.item,channel}) : undefined,
                                unbridge: bridged ? () => control.unbridge({cell:cell.cell.id,item:cell.item}) : undefined,
                                ungroup: canUngroup ? () => control.ungroup({cell:cell.cell.id}) : undefined,
                                midi: {
                                    assignment: cell.abgroup?.midi,
                                    choices: () => control.midi.abgroup.choices(cell.cell.id),
                                }
                            },
                            module: controllers.module,
                        },
                        onRender,
                        dragSeer: $onDrag,
                        ineligible: !!dragging && (!($drag.source || $drag.dest || $drag.proc) || dzones && noReceive(dzones)),
                        overlay,
                    }
                )
            }
            // we are iterating backwards through each lane and this captures the "next" item in the lane.
            // it is used to make decisions about formatting.
            $next = $lane[0]
        }
    }

    let key = 0
    return (
        <D k={["chain-matrix"]}>
            {$lanes.map((lane,i) =>
                <D key={++key}>
                    {lane.map(cell => <MatrixCellItem midi={midi} key={key++} {...cell}/>)}
                </D>
            )}
        </D>
    )
}

interface IChainElement {
    k: TKlass
    s?: any
    onClick?: (e:any)=>void
    onMouseLeave?: ()=>void
}

interface IChainHeaderBase {
    color: {
        fg: string,
        bg: string,
    }
    name: IChainElement
    fave: IChainElement
    lock: IChainElement
    normal: IChainElement
    target: IChainElement
    notes: IChainElement
    hide?: IChainElement
    tags?: IChainElement
}

function ChainHeaderBase (
    {
        chain,
        control,
        arch,
    }:
    {
        chain: IChain
        control: IChainBoxController
        arch: IArch
    }
) : IChainHeaderBase
{
    const {
        active,
    } = chain.cond

    const {
        fave,
        normal,
    } = chain.state

    const invalid = Chain.invalid(chain,arch)
    const {state} = chain
    const {fave:onFave,lock:onLock,focus:onFocus,normal:onNormal} = control||{}

    // Figma user-color formula (Colors page, frames 491/492): text and glyphs
    // are the pure palette color, backgrounds are the color at 20% opacity.
    // Muting is opacity/filter in CSS, not a color transform.
    const color = {
        bg: `color-mix(in srgb, ${state.color} 20%, transparent)`,
        fg: state.color,
    }

    // spans + metadata
    const $ = {
        color,
        name: { k:['name'] },
        fave: {
            k: ['fave','acticon','mui-icons'],
            ...onClick(onFave),
            child: fave ? 'bookmark' : 'bookmark_border',
        },
        lock: {
            k: ['lock','acticon','symbols'],
            ...onClick(onLock),
            child: 'lock',
        },
        normal: {
            k: ['normal','acticon',invalid && 'disabled',normal && 'active'],
            ...onClick(!invalid ? onNormal : undefined),
            child: 'N',
        },
        target: {
            k: ['target','acticon'],
            ...onClick(onFocus),
            child: <TargetIcon size={18} active={!!chain.cond.focus}/>,
        },
        notes: {
            k: ['notes','acticon','symbols'],
            child: 'description',
        },
    }
    return $
}

function verifyChain (chain: IChain, arch: IArch) : string | undefined {
    const invalid = Chain.invalid(chain,arch)
    return invalid === Chain.Invalid.BadNode
        ? 'Chain has invalid/incomplete nodes.'
        : invalid === Chain.Invalid.NoPath
        ? 'Chain has no valid signal paths. A chain must have a source and destination.'
        : invalid === Chain.Invalid.Conflict
        ? 'Chain has active nodes with overlapping Port IDs.'
        : undefined
}

export function BrowserChain (
    {
        chain,
        control,
        arch,
    }:
    {
        chain: IChain
        control: IChainBoxController
        arch: IArch
    })
{
    const {cond:{active,hidden,selected},state} = chain
    const invalid = verifyChain(chain,arch)

    const {
        remove:onDelete,
        name:onName,
        hide:onHide,
        show:onShow,
        copy:onCopy,
    } = control

    const $ = ChainHeaderBase({chain,control,arch})
    const fg = {color: $.color.fg}
    // the 20%/30% fills live in CSS on .chain-box so :hover can restyle them
    const bg = {'--chain-color': $.color.fg}

    $.normal.s = state.normal ? fg : {}
    $.fave.s = state.fave ? fg : {}

    $.hide = {
        k: ['hide','acticon'],
        ...onClick(e => hidden ? onShow(!!e.shiftKey) : onHide()),
        child: <span className="symbols">{!hidden ? 'visibility' : 'visibility_off'}</span>,
        s: fg,
    }

    const onActive =
        invalid != null
        ? () => control.alert({
            title: 'CHAIN INVALID',
            message: invalid
        })
        : control.active

    const activate = {
        k: ['activate', invalid ? 'invalid' : active && 'active'],
        child: !invalid 
            ? <D
                k={['button',active && 'active']}
                s={{
                    backgroundColor: active
                        ? $.color.fg 
                        : $.color.bg, 
                    borderColor: $.color.fg,
                }}
                {...onClick(onActive)}/>
            : <D k={['button','invalid']} {...onClick(onActive)}/>
    }
    const bypassed = !!chain.cond.bypass

    return (
        <D  k={[
                'chain-box',
                selected && 'highlight',
                active ? 'active' : 'inactive',
                bypassed && 'bypassed',
                hidden && 'hidden'
            ]}
            s={bg}
        >
            <D {...activate}/>
            <EditableTextBox {...{
                k:[$.name.k],
                value: state.name,
                onSave: v => {
                    onName && onName(v||"")
                    return v
                },
                editOn: 'dblclick',
                s: fg,
            }}/>
            {chain.state.locked && <D {...$.lock} s={fg}/>}
            { bypassed &&
                <D k="overlay" onClick={e => {
                    if (chain.state.locked) {
                        toast(<Div k='toast'>Chain is locked</Div>, { id: 'toast', style: TOAST_STYLE })
                    } else {
                        control.bypass?.()
                    }
                    e.stopPropagation()
                    e.preventDefault()
                }}>
                    <h1>Processors Bypassed</h1>
                    <h2>(click to restore)</h2>
                </D>
            }
            <D {...$.hide}/>
            <D k='div'/>
            <D {...$.fave}/>
            <D {...$.normal}/>
            <TriPop
                    k={!active ? 'mute' : ''}
                    s={fg}
                    items={[
                        {
                            label: chain.state.locked ? 'Unlock' : 'Lock',
                            choose: control.lock,
                        },
                        control.bypass && {
                            label: chain.cond.bypass ? 'Restore Processors' : 'Bypass Processors',
                            choose: control.bypass,
                            disabled: !!chain.state.locked,
                        },
                        onCopy && {
                            label:'Duplicate',
                            choose: onCopy,
                        },
                        onDelete && {
                            label:'Delete',
                            choose: onDelete,
                            disabled: !!chain.state.locked,
                        },
                        {
                            divider: 'top',
                            label: `MIDI ${
                                control.midi.assignment
                                ? `${MidiNote.toString(control.midi.assignment)}`
                                : ''
                            }`,
                            choices: control.midi.choices,
                        },
                        {
                            divider: 'top',
                            label:
                                <D k={["color-picker"]}>
                                    <ColorPicker
                                        color={chain.state.color}
                                        onChange={color => control.color(color)}
                                    />
                                </D>
                        }
                    ]}/>
        </D>
    )
}

interface IChainHeader extends IChainHeaderBase {
    minimize?: IChainElement
}

import colors from './colors'

// Round swatches, evenly distributed; the selected one gets a neutral outline
// ring (not a color-on-white fill like the old @uiw/react-color-circle)
function ColorPicker ({color,onChange=(_)=>{}}:{color:string,onChange?:(color:string)=>void}) {
    const selected = color?.toLowerCase()
    return (
        <div className='swatches'>
            {colors.map(c =>
                <div key={c}
                    className={'swatch' + (c.toLowerCase() === selected ? ' selected' : '')}
                    style={{backgroundColor: c}}
                    onClick={() => onChange(c)}
                />
            )}
        </div>
    )
}

export function ChainHeader ({chain,midi,control,_new,arch} : {
    chain: IChain
    midi?: IMidiLearn
    control:IChainHeaderController
    _new?: boolean
    arch: IArch
})
{
    const {cond:{active,focus,selected},state} = chain
    const invalid = verifyChain(chain,arch)

    const {color} = state
    const {
        remove:onDelete,
        name:onName,
        hide:onHide,
        split:onSplit,
        unsplit:onUnsplit,
        copy:onCopy,
    } = control

    const $ = ChainHeaderBase({chain,control,arch}) as IChainHeader
    const fg = {color: $.color.fg}

    const [showTags,setShowTags] = useState<boolean>(false)

    const learner = {chain:chain.id}
    const onActive = midi && !invalid
        ? () => midi.learn(learner)
        : (
            invalid
            ? () => control.alert({
                title: 'CHAIN INVALID',
                message: invalid
            })
            : control.active
        )
    const activate = {
        k: ['activate', invalid ? 'invalid' : active && 'active'],
        child: !invalid
            ? <D
                k={['button',active && 'active',midi?.state(learner) && 'midi-learn', midi?.state(learner) === 'learning' && 'learning']}
                s={{
                    backgroundColor:
                        !midi && active
                            ? $.color.fg
                            : $.color.bg,
                    borderColor: !midi && $.color.fg
                }}
                {...onClick(onActive)}/>
            : <D k={['button','invalid']} {...onClick(onActive)}/>
    }

    $.minimize = {
        k: ['minimize','acticon'],
        s: fg,
        ...onClick(onHide),
        child: <span className="symbols">remove</span>,
    }
    $.normal.s = state.normal ? { color } : {}
    $.fave.s = state.fave ? { color } : {}
    // Target icon colors are intrinsic (red/gray), not chain color
    $.tags = {
        k: ['tags','acticon',showTags ? 'mui-icons' : 'symbols',showTags && 'selected'],
        ...onClick(() => setShowTags(!showTags)),
        child: 'sell',
        s: showTags ? {color} : {}
    }

    return (
        <D k={['chain-header',
                Chain.isSplit(chain) && 'split',
                active && !invalid && 'active',
                selected && 'highlight',
                focus && 'focus',
                !chain.state.locked && 'deletable',
                ]}>
            <D k='header'
                /* Figma washes the active chain header with 10% accent (node 16:1542);
                   an OFF chain keeps its color bar in the muted prod shade (Gosta, round 6) */
                s={{background: active && !invalid
                    ? `color-mix(in srgb, ${$.color.fg} 10%, transparent)`
                    : applySat(applyLum($.color.fg,0.15),0.5)}}
                onClickCapture={e => {
                    if (e.button === 0 && Events.cmd(e) && !chain.state.locked) {
                        onDelete()
                        e.stopPropagation()
                        e.preventDefault()
                    }
                }}>
                <D {...activate}/>
                <EditableTextBox {...{
                    k:[$.name.k],
                    value: state.name,
                    onSave: v => {
                        onName && onName(v||"")
                        return v
                    },
                    active: !!_new,
                    editOn: 'dblclick',
                    s: fg,
                }}/>
                {chain.state.locked && <D {...$.lock} s={fg}/>}
                <D {...$.minimize}/>
                <TriPop
                    s={fg}
                    items={[
                        {
                            label: chain.state.locked ? 'Unlock' : 'Lock',
                            choose: control.lock,
                        },
                        control.bypass && {
                            label: 'Bypass Processors',
                            choose: control.bypass,
                            disabled: !!chain.state.locked,
                        },
                        onSplit && {
                            label:'Split',
                            choose: onSplit,
                            disabled: !!chain.state.locked,
                        },
                        onUnsplit && {
                            label:'Unsplit',
                            choose: onUnsplit,
                            disabled: !!chain.state.locked,
                        },
                        onCopy && {
                            label:'Duplicate',
                            choose: onCopy,
                        },
                        onDelete && {
                            divider: 'top',
                            label:'Delete',
                            choose: onDelete,
                            disabled: !!chain.state.locked,
                        },
                        {
                            divider: 'top',
                            label: `MIDI ${
                                control.midi.assignment
                                ? `${MidiNote.toString(control.midi.assignment)}`
                                : ''
                            }`,
                            choices: control.midi.choices,
                        },
                        {
                            label:
                                <D k={['color-picker']}>
                                    <ColorPicker
                                        color={chain.state.color}
                                        onChange={color => control.color(color)}
                                    />
                                </D>
                        }
                    ]}/>
            </D>
            <D k='subheader'>
                <D k='left'>
                    <D {...$.target}/>
                </D>
                <D k='right'>
                    <D {...$.normal}/>
                    {/* <D {...$.notes}/> */}
                    <D {...$.tags}/>
                    <D {...$.fave}/>
                </D>
            </D>
            {showTags &&
                <D k='chain-tags'>
                    <Tags
                        tags={chain.state.tags}
                        name={chain.state.name}
                        control={control.tags}
                    />
                </D>
            }
        </D>
    )
}

export function HorzChainLink ({terminal, thru, tee, short, split, left, right, children,spacer, k=[]}
    : {
        terminal?:boolean
        thru?:boolean
        tee?:boolean
        short?:boolean
        split?:boolean
        left?:boolean
        right?:boolean
        spacer?: boolean
        children?:any
        k?:Array<string|string[]>
    } = {})
{

    const $k = ['chain-link',terminal && 'terminal',split && 'split',(right || left) && right ? 'right' : 'left']
    return !spacer
        ?
            <D k={$k}>
                <D k={['left',thru && 'thru',thru && tee && 'tee',short && !terminal && 'short']}/>
                {(thru || left) && <D k={['thru']}/>}
                {terminal && <D k={['terminal']}/>}
                {children && <D k={['icon',k]}>{children}</D>}
            </D>
        :
            // THIS IS USED FOR SPACING, essentially
            <D k={$k}/>
}

export function VertChainLink ({ terminal, short, split, left, right, spacer}
    : {
        terminal?:boolean
        short?:boolean
        split?:boolean
        left?:boolean
        right?:boolean
        spacer?:boolean
    } = {})
{
    const $k=['chain-link',terminal && 'terminal',split && 'split',(right || left) && right ? 'right' : 'left']
    return !spacer
        ?
            <D k={$k}>
                <D k={['vert',short && !terminal && 'short']}/>
                {terminal && <D k={['terminal']}/>}
            </D>
        :
            // THIS IS USED FOR SPACING, essentially
            <D k={$k}/>
}

export type TConflictDialogArgs = {
    title?: string
    conflicts: Array<{
        chain: IChain
        nodes: Array<{module: IModule, ports: string[]}>
    }>
    message: string|ReactElement
    confirm () : void
    resolution: {
        prompt: string
        choices: string[]
        primary: string
        secondary?: string
    }
    dismiss() : void
}

export function ConflictDialog ({
    title = 'CONFLICTS DETECTED',
    conflicts,
    message,
    confirm,
    resolution,
    dismiss,
} : TConflictDialogArgs)
{
    const ref = useRef<HTMLDivElement>(null)
    const [expandIndex,setExpandIndex] = useState<number>(0)

    return (
        <Div k={['modal','conflicts']} s={{display:'block'}} ref={ref}>
            <D k='dialog'>
                <D k='titlebar'>
                    <D k='title'>{title}</D>
                    <D k={'dismiss'} {...onClick(dismiss)}>close</D>
                </D>
                <D k='body'>
                    <D k='message'>{message}</D>
                    { 
                        conflicts.map((c,i) => 
                            <D k='conflict' {...onClick(() => {
                                setExpandIndex(i === expandIndex ? -1 : i)
                            })}>
                                <D k='header'>
                                    <D k={['expand','symbols']}>{
                                        expandIndex === i
                                        ? 'keyboard_arrow_down'
                                        : 'keyboard_arrow_right'
                                    }</D>
                                    <D k='chain'>{c.chain.state.name}</D>
                                    <D k='score'>{c.nodes.length} nodes</D>
                                </D>
                                { expandIndex === i &&
                                    <D k='nodes'>
                                        { c.nodes.map(({module, ports}) => <D k='node'>{`${module.device.name} → ${module.name} (${ports.join(', ')})`}</D>) }
                                    </D>
                                }
                            </D>
                        )
                    }
                    <D k='message'>{resolution.prompt}
                        <ul>{resolution.choices.map(_ => <li className='choice'>{_}</li>)}</ul>
                    </D>
                    <D k='actions'>
                        <D k={['cta','secondary']} {...onClick(dismiss)}>{resolution.secondary ?? 'CANCEL'}</D>
                        <D k={['cta','primary']} {...onClick(confirm)}>{resolution.primary}</D>
                    </D>
                </D>
            </D>
        </Div>
    )
}
